// Small building blocks used everywhere: cards, labels, pills, status dots,
// icon chips, skeletons and empty states.
import { cn } from '../../lib/format.js'

export function Card({ as: Component = 'div', className, interactive = false, ...props }) {
  return (
    <Component
      className={cn(
        'rounded-2xl bg-surface/85 ring-1 ring-inset ring-white/10 backdrop-blur-sm',
        interactive && 'transition-[box-shadow,transform,background-color] duration-200 hover:bg-surface hover:ring-white/20',
        className,
      )}
      {...props}
    />
  )
}

export function Eyebrow({ as: Component = 'p', className, ...props }) {
  return (
    <Component
      className={cn('text-[11px] font-semibold tracking-[0.16em] text-ink-muted uppercase', className)}
      {...props}
    />
  )
}

const PILL_TONES = {
  neutral: 'bg-white/5 text-ink ring-white/10',
  primary: 'bg-primary-500/10 text-primary-300 ring-primary-500/25',
  secondary: 'bg-secondary-500/10 text-secondary-300 ring-secondary-500/25',
  success: 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/25',
  danger: 'bg-red-500/10 text-red-300 ring-red-500/25',
  accent: 'tint-soft tint-text tint-ring',
}

export function Pill({ tone = 'neutral', icon: Icon, className, children, ...props }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ring-1 ring-inset',
        PILL_TONES[tone],
        className,
      )}
      {...props}
    >
      {Icon && <Icon aria-hidden="true" className="size-3.5" />}
      {children}
    </span>
  )
}

const DOT_TONES = {
  live: 'bg-live animate-live-pulse',
  open: 'bg-open',
  upcoming: 'bg-secondary-400',
  completed: 'bg-slate-400',
  cancelled: 'bg-red-400',
  draft: 'bg-slate-400',
}

// A colored dot with a text label; the color is never the only signal.
export function StatusDot({ status, label, className }) {
  return (
    <span className={cn('inline-flex items-center gap-2 text-xs font-medium', className)}>
      <span aria-hidden="true" className={cn('size-2 shrink-0 rounded-full', DOT_TONES[status] ?? DOT_TONES.completed)} />
      {label}
    </span>
  )
}

const CHIP_TONES = {
  primary: 'bg-primary-500/10 text-primary-400 ring-primary-500/25',
  secondary: 'bg-secondary-500/10 text-secondary-400 ring-secondary-500/25',
  neutral: 'bg-white/5 text-ink ring-white/10',
  live: 'bg-red-500/10 text-red-300 ring-red-500/25',
  accent: 'tint-soft tint-text tint-ring',
}

export function IconChip({ icon: Icon, tone = 'primary', size = 'md', className }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid shrink-0 place-items-center ring-1 ring-inset',
        size === 'sm' ? 'size-8 rounded-lg' : 'size-10 rounded-xl',
        CHIP_TONES[tone],
        className,
      )}
    >
      <Icon className={size === 'sm' ? 'size-4' : 'size-5'} />
    </span>
  )
}

export function Skeleton({ className }) {
  return <span aria-hidden="true" className={cn('block animate-pulse rounded-lg bg-white/[0.06]', className)} />
}

// `compact` is a single row, for sections that sit side by side.
export function EmptyState({ icon: Icon, title, children, action, compact, className }) {
  if (compact) {
    return (
      <div className={cn('flex items-center gap-3.5 px-5 py-4', className)}>
        {Icon && <IconChip icon={Icon} tone="neutral" />}
        <div className="min-w-0">
          <p className="font-medium text-ink">{title}</p>
          {children && <p className="text-sm text-ink-muted">{children}</p>}
        </div>
        {action && <div className="ml-auto shrink-0">{action}</div>}
      </div>
    )
  }
  return (
    <div className={cn('flex flex-col items-center gap-3 px-6 py-10 text-center', className)}>
      {Icon && <IconChip icon={Icon} tone="neutral" />}
      <p className="font-medium text-ink">{title}</p>
      {children && <p className="max-w-sm text-sm text-ink-muted">{children}</p>}
      {action}
    </div>
  )
}

// Page-width container with the standard 16px mobile gutter.
export function Container({ as: Component = 'div', className, ...props }) {
  return <Component className={cn('mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8', className)} {...props} />
}
